import { useKeycloak } from "@react-keycloak/web";
import { useNavigate } from "react-router-dom";
import {
  useAdmin,
  useAppSelector,
  useBrowserExtensionStore,
} from "../utils/hooks";
import { useSearchParams } from "react-router-dom";
import { isSafari } from "react-device-detect";
import { INSTITUTIONAL_SUBSCRIPTION_PAGE_ENDPOINT } from "./endpointConstants";
import { useStripeCheckout } from "../hooks/useStripeCheckout";
import { useSnackbar } from "notistack";
import { checkIfExtensionInstalled } from "../utils/utils";
import { EX_PREMIUM_ECOMMERCE_SUBTYPE_NAME } from "../utils/constants";
import { useEffect } from "react";

export const RequiredAuth = ({ children }: { children: JSX.Element }) => {
  const { keycloak } = useKeycloak();

  if (keycloak && keycloak.authenticated === false) {
    keycloak.login();
    return null;
  }

  return children;
};

export const RequiredReg = ({ children }: { children: JSX.Element }) => {
  const { keycloak } = useKeycloak();

  if (keycloak && keycloak.authenticated === false) {
    keycloak.register();
    return null;
  }

  return children;
};

export const RequiredAdmin = ({ children }: { children: JSX.Element }) => {
  const { keycloak } = useKeycloak();
  const { isAdmin } = useAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    if (keycloak?.authenticated && !isAdmin) {
      console.warn(
        `Non-admin user attempted to access admin page. Redirecting home.`,
      );
      navigate("/", { replace: true });
    }
  }, [keycloak?.authenticated, isAdmin, navigate]);

  if (keycloak && keycloak.authenticated === false) {
    keycloak.login();
    return null;
  }

  if (!isAdmin) {
    return null; // Will redirect via useEffect
  }

  return children;
};

export const RequiredMarketingFlow = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromMarketing = searchParams.get("from") === "marketing";
  const plan = searchParams.get("plan");
  const extensionStoreLink = useBrowserExtensionStore();
  const subscription = useAppSelector(
    (state) => state.consumerSubscriptions.subscription,
  );
  const { startStripeCheckout } = useStripeCheckout();
  const { enqueueSnackbar } = useSnackbar();

  // If not from marketing, render normally
  if (!fromMarketing) {
    return children;
  }

  // If not authenticated, go to registration
  if (keycloak && !keycloak.authenticated) {
    keycloak.register();
    return null;
  }

  // If authenticated and from marketing, check plan and redirect
  if (keycloak?.authenticated) {
    const isAnnualUser = plan === "annual";
    const isFreeUser = plan === "free";
    const isInstituteUser = plan === "institutional";

    if (isInstituteUser) {
      console.log("institutional flow, subscription:", subscription);
      // Set a flag to open the connect dialog after redirect
      navigate(INSTITUTIONAL_SUBSCRIPTION_PAGE_ENDPOINT);
      return null;
    } else if (isFreeUser && !isSafari && extensionStoreLink) {
      // Redirect free users to extension store
      if (!checkIfExtensionInstalled()) {
        window.location.href = extensionStoreLink;
        return null;
      }
    } else if (isAnnualUser) {
      const handleStripeCheckout = async (duration: string) => {
        try {
          await startStripeCheckout(duration);
        } catch (error) {
          enqueueSnackbar("Failed to start checkout. Please try again.", {
            variant: "error",
            autoHideDuration: 5000,
          });
          console.error("Stripe checkout error:", error);
        }
      };

      // Only trigger checkout if the user does NOT already have annual
      if (
        !subscription?.subscriptions?.find(
          (s) => s.productSubType?.name === EX_PREMIUM_ECOMMERCE_SUBTYPE_NAME,
        )
      ) {
        handleStripeCheckout("YEARLY");
        return null;
      }
      // If user already has annual, skip checkout (could redirect or show a message)
      enqueueSnackbar("You already have an active Annual plan.", {
        variant: "info",
        autoHideDuration: 5000,
      });
      return null;
    } else {
      // Normal extension flow
      navigate("/?p=extension");
      return null;
    }
  }

  // If keycloak isn't initialized yet, show nothing
  return null;
};
